import type { AestheticsRecord, CustomPanel } from './types';

export const profileSectionIds = [
  'latest',
  'blurbs',
  'music',
  'friends',
  'comments',
  'replies',
  'interests',
] as const;

export type ProfileSectionId = typeof profileSectionIds[number];
export type CustomPanelSectionId = `custom:${string}`;
export type ProfileLayoutSectionId = ProfileSectionId | CustomPanelSectionId;

export interface ProfileLayout {
  left: ProfileLayoutSectionId[];
  right: ProfileLayoutSectionId[];
  hidden: ProfileLayoutSectionId[];
}

export const defaultProfileLayout: ProfileLayout = {
  left: ['music', 'interests'],
  right: ['latest', 'blurbs', 'friends', 'comments', 'replies'],
  hidden: [],
};

const validSections = new Set<string>(profileSectionIds);

export function isProfileSectionId(value: string): value is ProfileSectionId {
  return validSections.has(value);
}

export function customPanelSectionId(id: string): CustomPanelSectionId {
  return `custom:${id}`;
}

export function customPanelIdFromSection(section: string): string | undefined {
  return section.startsWith('custom:') ? section.slice(7) : undefined;
}

export function customPanelSlots(stored: CustomPanel[] = []): CustomPanel[] {
  return [0, 1, 2].map(index => stored[index] || {
    id: `box-${index + 1}`,
    title: '',
    type: 'text',
    content: '',
  });
}

export function normalizeProfileLayout(
  stored?: AestheticsRecord['layout'],
  customPanels: CustomPanel[] = [],
): ProfileLayout {
  const storedPlacements = new Set([...(stored?.left || []), ...(stored?.right || [])]);
  const validCustomSections = new Set(customPanels.map(panel => customPanelSectionId(panel.id)));
  const valid = (value: string): value is ProfileLayoutSectionId => (
    validSections.has(value) || validCustomSections.has(value as CustomPanelSectionId)
  );
  const seen = new Set<ProfileLayoutSectionId>();
  const clean = (values?: string[]): ProfileLayoutSectionId[] => (values || []).flatMap(value => {
    if (!valid(value) || seen.has(value)) return [];
    const section = value as ProfileLayoutSectionId;
    seen.add(section);
    return [section];
  });
  const left = clean(stored?.left);
  const right = clean(stored?.right);
  for (const section of defaultProfileLayout.left) {
    if (!seen.has(section)) {
      seen.add(section);
      left.push(section);
    }
  }
  for (const section of defaultProfileLayout.right) {
    if (!seen.has(section)) {
      seen.add(section);
      right.push(section);
    }
  }
  for (const panel of customPanels) {
    const section = customPanelSectionId(panel.id);
    if (!seen.has(section)) {
      seen.add(section);
      right.push(section);
    }
  }
  const hiddenSeen = new Set<ProfileLayoutSectionId>();
  const hidden = (stored?.hidden || []).flatMap(value => {
    const section = value as ProfileLayoutSectionId;
    if (!valid(value) || hiddenSeen.has(section)) return [];
    hiddenSeen.add(section);
    return [section];
  });
  for (const panel of customPanels) {
    const section = customPanelSectionId(panel.id);
    if (!storedPlacements.has(section) && !hiddenSeen.has(section)) {
      hiddenSeen.add(section);
      hidden.push(section);
    }
  }
  return { left, right, hidden };
}
